import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateTimeKST } from '../utils/dateUtils';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Typography,
  Collapse,
  CircularProgress,
  Switch,
  FormControlLabel,
  InputAdornment,
  Skeleton,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Refresh,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search,
  Clear
} from '@mui/icons-material';
import {
  fetchTradingPairs,
  createTradingPair,
  updateTradingPair,
  deleteTradingPair
} from '../store/tradingPairSlice';
import { fetchExchanges } from '../store/exchangesSlice';
import { ExchangeIcon, CryptoIcon } from '../components/IconDisplay';
import api from '../services/api';
import { STATUS_POLL_MS, SLOW_REFRESH_MS } from '../config/intervals';
import { useConfirm } from '../components/ConfirmDialog';

// Module-scope so the component reference is stable across TradingPairs
// re-renders. Previously defined inline inside TradingPairs, which meant
// every keystroke in the Add/Edit dialog re-created the component type and
// forced every expanded OrderbookDisplay to unmount and remount.
const formatOrderbookPrice = (price) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 }).format(price);
const formatOrderbookQty = (qty) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 8 }).format(qty);

const OrderbookDisplay = React.memo(function OrderbookDisplay({ data }) {
  if (!data) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        <CircularProgress size={20} />
        <Typography variant="caption" color="textSecondary" mt={1}>
          Loading orderbook data...
        </Typography>
      </Box>
    );
  }
  return (
    <Box p={2}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="subtitle2" gutterBottom>
            {data.target?.exchange} ({data.target?.symbol})
          </Typography>
          {data.target?.data ? (
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="error">Asks</Typography>
                {data.target.data.asks?.slice(0, 5).map((ask, idx) => (
                  <Box key={idx} display="flex" justifyContent="space-between" fontSize="0.75rem">
                    <Box component="span" sx={{ color: 'error.main' }}>{formatOrderbookPrice(ask[0])}</Box>
                    <span>{formatOrderbookQty(ask[1])}</span>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="success">Bids</Typography>
                {data.target.data.bids?.slice(0, 5).map((bid, idx) => (
                  <Box key={idx} display="flex" justifyContent="space-between" fontSize="0.75rem">
                    <Box component="span" sx={{ color: 'success.main' }}>{formatOrderbookPrice(bid[0])}</Box>
                    <span>{formatOrderbookQty(bid[1])}</span>
                  </Box>
                ))}
              </Grid>
            </Grid>
          ) : (
            <Typography variant="caption" color="textSecondary">No data</Typography>
          )}
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle2" gutterBottom>
            {data.origin?.exchange} ({data.origin?.symbol})
          </Typography>
          {data.origin?.data ? (
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="error">Asks</Typography>
                {data.origin.data.asks?.slice(0, 5).map((ask, idx) => (
                  <Box key={idx} display="flex" justifyContent="space-between" fontSize="0.75rem">
                    <Box component="span" sx={{ color: 'error.main' }}>{formatOrderbookPrice(ask[0])}</Box>
                    <span>{formatOrderbookQty(ask[1])}</span>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="success">Bids</Typography>
                {data.origin.data.bids?.slice(0, 5).map((bid, idx) => (
                  <Box key={idx} display="flex" justifyContent="space-between" fontSize="0.75rem">
                    <Box component="span" sx={{ color: 'success.main' }}>{formatOrderbookPrice(bid[0])}</Box>
                    <span>{formatOrderbookQty(bid[1])}</span>
                  </Box>
                ))}
              </Grid>
            </Grid>
          ) : (
            <Typography variant="caption" color="textSecondary">No data</Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
});


// Form constants moved to module scope so they are stable across renders
// and can be shared with the dialog component.
const QUOTE_ASSET_CHOICES = [
  { value: 'KRW', label: 'KRW' },
  { value: 'USDT', label: 'USDT' },
  { value: 'USDC', label: 'USDC' },
  { value: 'BTC', label: 'BTC' },
  { value: 'USD', label: 'USD' },
];

const EMPTY_TRADING_PAIR_FORM = {
  target_symbol: '',
  origin_symbol: '',
  target_exchange: '',
  target_quote_asset: '',
  origin_exchange: '',
  origin_quote_asset: '',
  is_active: true,
  max_orders_per_s: 3,
  enable_hedge_monitoring: true,
  hedge_tolerance_rate: '1.0',
};

function validateTradingPairForm(formData) {
  const errors = {};
  if (!formData.target_symbol.trim()) errors.target_symbol = 'Target symbol is required';
  if (!formData.origin_symbol.trim()) errors.origin_symbol = 'Origin symbol is required';
  if (!formData.target_exchange) errors.target_exchange = 'Target exchange is required';
  if (!formData.target_quote_asset) errors.target_quote_asset = 'Target quote asset is required';
  if (!formData.origin_exchange) errors.origin_exchange = 'Origin exchange is required';
  if (!formData.origin_quote_asset) errors.origin_quote_asset = 'Origin quote asset is required';
  if (
    formData.target_exchange && formData.origin_exchange &&
    formData.target_exchange === formData.origin_exchange &&
    formData.target_quote_asset === formData.origin_quote_asset &&
    formData.target_symbol === formData.origin_symbol
  ) {
    errors.origin_exchange = 'Target and origin sides cannot be identical (same exchange + same quote + same symbol)';
  }
  return errors;
}

/**
 * Self-contained Add/Edit dialog that owns its own formData state. The
 * previous implementation lifted formData up into TradingPairs, so every
 * keystroke re-rendered the entire page including the 100+ row table —
 * visible as noticeable typing lag in the symbol field. By keeping form
 * state local here, typing only re-renders the dialog itself.
 *
 * The parent hands us `editingPair` (or null for create) and gets a
 * `submitData` object back through `onSubmit`; it stays responsible for
 * the actual dispatch + error surfacing because that ties into Snackbar.
 */
const TradingPairDialog = React.memo(function TradingPairDialog({
  open,
  editingPair,
  exchanges,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = React.useState(EMPTY_TRADING_PAIR_FORM);
  const [formErrors, setFormErrors] = React.useState({});

  // Re-seed the form whenever the dialog opens.
  React.useEffect(() => {
    if (!open) return;
    if (editingPair) {
      // Editing existing pair: prefer the per-side fields the API now
      // returns. Fall back to the legacy `symbol` field for any stale rows
      // that may not yet be re-serialized.
      const t = editingPair.target_symbol || editingPair.symbol || '';
      const o = editingPair.origin_symbol || editingPair.symbol || '';
      setFormData({
        target_symbol: t,
        origin_symbol: o,
        target_exchange: editingPair.target_exchange ?? '',
        target_quote_asset: editingPair.target_quote_asset || '',
        origin_exchange: editingPair.origin_exchange ?? '',
        origin_quote_asset: editingPair.origin_quote_asset || '',
        is_active: !!editingPair.is_active,
        max_orders_per_s: editingPair.max_orders_per_s || 3,
        enable_hedge_monitoring: !!editingPair.enable_hedge_monitoring,
        hedge_tolerance_rate: editingPair.hedge_tolerance_rate
          ? (parseFloat(editingPair.hedge_tolerance_rate) * 100).toString()
          : '1.0',
      });
    } else {
      setFormData(EMPTY_TRADING_PAIR_FORM);
    }
    setFormErrors({});
  }, [open, editingPair]);

  // Sort exchanges once per exchanges-prop change.
  const sortedExchanges = React.useMemo(() => {
    return [...(exchanges || [])].sort((a, b) => {
      if (a.name !== b.name) return a.name.localeCompare(b.name);
      return a.market_type.localeCompare(b.market_type);
    });
  }, [exchanges]);

  const handleFormChange = React.useCallback((field) => (event) => {
    let value;
    if (field === 'enable_hedge_monitoring') {
      value = event.target.checked;
    } else {
      value = event.target.value;
      if (field === 'target_symbol' || field === 'origin_symbol') {
        value = value.toUpperCase();
      }
    }
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Convenience: when the user types into the target symbol field and
      // the origin symbol is still empty (or had been mirroring target),
      // auto-mirror. Most pairs use identical tickers on both sides.
      if (field === 'target_symbol' && (!prev.origin_symbol || prev.origin_symbol === prev.target_symbol)) {
        next.origin_symbol = value;
      }
      return next;
    });
    setFormErrors(prev => prev[field] ? { ...prev, [field]: null } : prev);
  }, []);

  const handleSubmit = React.useCallback(() => {
    const errors = validateTradingPairForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const submitData = {
      ...formData,
      hedge_tolerance_rate: parseFloat(formData.hedge_tolerance_rate) / 100,
    };
    onSubmit(submitData);
  }, [formData, onSubmit]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingPair ? 'Edit Trading Pair' : 'Create New Trading Pair'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Target Symbol"
                value={formData.target_symbol}
                onChange={handleFormChange('target_symbol')}
                error={!!formErrors.target_symbol}
                helperText={formErrors.target_symbol || 'Ticker on target exchange (e.g., BTC)'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Origin Symbol"
                value={formData.origin_symbol}
                onChange={handleFormChange('origin_symbol')}
                error={!!formErrors.origin_symbol}
                helperText={formErrors.origin_symbol || 'Ticker on origin exchange (auto-mirrored from target — same underlying asset)'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.target_exchange} required>
                <InputLabel>Target Exchange</InputLabel>
                <Select
                  value={formData.target_exchange}
                  onChange={handleFormChange('target_exchange')}
                  label="Target Exchange"
                >
                  {sortedExchanges.map((exchange) => (
                    <MenuItem key={exchange.id} value={exchange.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ExchangeIcon exchangeName={exchange.name} size={20} />
                        {exchange.display_name || `${exchange.name} - ${exchange.market_type}`}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.target_exchange && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.target_exchange}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.target_quote_asset} required>
                <InputLabel>Target Quote Asset</InputLabel>
                <Select
                  value={formData.target_quote_asset}
                  onChange={handleFormChange('target_quote_asset')}
                  label="Target Quote Asset"
                >
                  {QUOTE_ASSET_CHOICES.map((asset) => (
                    <MenuItem key={asset.value} value={asset.value}>
                      {asset.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.target_quote_asset && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.target_quote_asset}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.origin_exchange} required>
                <InputLabel>Origin Exchange</InputLabel>
                <Select
                  value={formData.origin_exchange}
                  onChange={handleFormChange('origin_exchange')}
                  label="Origin Exchange"
                >
                  {sortedExchanges.map((exchange) => (
                    <MenuItem key={exchange.id} value={exchange.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ExchangeIcon exchangeName={exchange.name} size={20} />
                        {exchange.display_name || `${exchange.name} - ${exchange.market_type}`}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.origin_exchange && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.origin_exchange}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.origin_quote_asset} required>
                <InputLabel>Origin Quote Asset</InputLabel>
                <Select
                  value={formData.origin_quote_asset}
                  onChange={handleFormChange('origin_quote_asset')}
                  label="Origin Quote Asset"
                >
                  {QUOTE_ASSET_CHOICES.map((asset) => (
                    <MenuItem key={asset.value} value={asset.value}>
                      {asset.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.origin_quote_asset && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.origin_quote_asset}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Max Orders Per Second"
                type="number"
                value={formData.max_orders_per_s}
                onChange={handleFormChange('max_orders_per_s')}
                inputProps={{ min: 1, max: 100 }}
                helperText="Maximum number of orders per second (1-100)"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1, mb: 1 }}>
                Hedge Monitoring
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip
                title={editingPair?.is_spot_to_spot ? "Hedge monitoring is not available for SPOT-to-SPOT pairs" : ""}
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enable_hedge_monitoring}
                      onChange={handleFormChange('enable_hedge_monitoring')}
                      disabled={editingPair?.is_spot_to_spot}
                    />
                  }
                  label="Enable Hedge Monitoring"
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tolerance Rate"
                type="number"
                value={formData.hedge_tolerance_rate}
                onChange={handleFormChange('hedge_tolerance_rate')}
                error={!!formErrors.hedge_tolerance_rate}
                helperText={formErrors.hedge_tolerance_rate || "Alert when position difference exceeds this %"}
                inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                disabled={!formData.enable_hedge_monitoring || editingPair?.is_spot_to_spot}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {editingPair ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

function TradingPairs() {
  const dispatch = useDispatch();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { tradingPairs, loading, filters } = useSelector((state) => state.tradingPairs);
  const { exchanges } = useSelector((state) => state.exchanges);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPair, setEditingPair] = useState(null);
  // Dialog state — formData lives inside TradingPairDialog now so that
  // typing does not re-render the 100+ row table.
  const [expandedRows, setExpandedRows] = useState({});
  const [orderbookData, setOrderbookData] = useState({});
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const closeSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };


  useEffect(() => {
    dispatch(fetchTradingPairs(filters));
    dispatch(fetchExchanges());

    // Periodic refresh fallback (WebSocket push is primary)
    const interval = setInterval(() => {
      dispatch(fetchTradingPairs(filters));
    }, SLOW_REFRESH_MS);

    return () => clearInterval(interval);
  }, [dispatch, filters]);

  // Fetch connection status every 2 seconds
  useEffect(() => {
    const fetchConnectionStatus = async () => {
      try {
        const response = await api.get('/api/orderbook-status/active_connections/');
        const data = response.data;
          const statusMap = {};
          data.forEach(status => {
            statusMap[status.trading_pair_id] = status;
          });
        setConnectionStatuses(statusMap);
      } catch (error) {
        console.error('Failed to fetch connection status:', error);
      }
    };

    fetchConnectionStatus();
    const statusInterval = setInterval(fetchConnectionStatus, STATUS_POLL_MS);

    return () => clearInterval(statusInterval);
  }, []);

  // Stable identity for the set of active pair ids. tradingPairs reference
  // changes on every periodic refetch (SLOW_REFRESH_MS) even when nothing
  // actually moved, which would otherwise reset the polling interval and
  // fire an immediate fetch-all on every tick. Keying the effect on a sorted
  // id string of only the active pairs keeps the cadence at STATUS_POLL_MS.
  const activePairIdsKey = useMemo(
    () => tradingPairs
      .filter(pair => pair.is_active)
      .map(pair => pair.id)
      .sort((a, b) => a - b)
      .join(','),
    [tradingPairs]
  );

  // Fetch orderbook data for all active pairs in one request. The expanded
  // row still receives full orderbook depth, but we avoid one REST request
  // and one React state update per pair every STATUS_POLL_MS.
  useEffect(() => {
    if (!activePairIdsKey) return;
    const activeIds = activePairIdsKey.split(',').map(Number);
    const activePairIdSet = new Set(activeIds);

    const fetchAllOrderbookData = async () => {
      try {
        const response = await api.get('/api/orderbook-status/bulk_orderbooks/', {
          params: { trading_pair_ids: activeIds.join(',') },
        });
        const results = response.data?.results || {};

        setOrderbookData(() => {
          const next = {};
          activeIds.forEach(pairId => {
            next[pairId] = results[String(pairId)] || null;
          });
          return next;
        });
      } catch (error) {
        console.error('Failed to fetch orderbook data:', error);
        setOrderbookData(prev => {
          const next = {};
          Object.keys(prev).forEach(pairId => {
            if (activePairIdSet.has(parseInt(pairId))) {
              next[pairId] = prev[pairId];
            }
          });
          return next;
        });
      }
    };

    fetchAllOrderbookData();
    const orderbookInterval = setInterval(fetchAllOrderbookData, STATUS_POLL_MS);
    return () => clearInterval(orderbookInterval);
  }, [activePairIdsKey]);

  const handleRefresh = () => {
    dispatch(fetchTradingPairs(filters));
    dispatch(fetchExchanges());
  };

  // Helper function to check if both orderbooks have data
  const hasBothOrderbookData = (pairId) => {
    const data = orderbookData[pairId];
    if (!data) return false;

    const hasSideData = (side) => {
      if (!side) return false;
      if (typeof side.has_data === 'boolean') return side.has_data;
      return Boolean(side.data?.bids?.length > 0 && side.data?.asks?.length > 0);
    };

    return hasSideData(data.target) && hasSideData(data.origin);
  };

  const handleOpenDialog = (pair = null) => {
    setEditingPair(pair);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPair(null);
  };

  // Receives the already-validated submitData from TradingPairDialog.
  const handleSubmit = async (submitData) => {
    try {
      if (editingPair) {
        await dispatch(updateTradingPair({ id: editingPair.id, data: submitData })).unwrap();
      } else {
        await dispatch(createTradingPair(submitData)).unwrap();
      }
      handleCloseDialog();
      // Refresh the list to ensure it's up to date
      dispatch(fetchTradingPairs(filters));
    } catch (error) {
      console.error('Failed to save trading pair:', error);
      
      // Parse error message
      let errorMessage = 'Failed to save trading pair';
      
      if (error && typeof error === 'object') {
        // Check for unique constraint error
        if (error.non_field_errors) {
          errorMessage = error.non_field_errors.join(', ');
        } else if (error.detail) {
          errorMessage = error.detail;
        } else if (error.message) {
          errorMessage = error.message;
        } else {
          // Try to extract field errors
          const fieldErrors = [];
          Object.keys(error).forEach(field => {
            if (Array.isArray(error[field])) {
              fieldErrors.push(`${field}: ${error[field].join(', ')}`);
            }
          });
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join('\n');
          }
        }
      }
      
      showError(errorMessage);
    }
  };

  const handleDelete = async (pairId) => {
    const ok = await confirm({
      title: 'Delete trading pair?',
      message: 'This also removes any triggers and subscriptions bound to the pair. Historical trades remain intact.',
      confirmLabel: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await dispatch(deleteTradingPair(pairId)).unwrap();
      // Refresh the list after deletion
      dispatch(fetchTradingPairs(filters));
    } catch (error) {
      console.error('Failed to delete trading pair:', error);
    }
  };

  const handleToggleActive = async (pair) => {
    try {
      await dispatch(updateTradingPair({ 
        id: pair.id, 
        data: { is_active: !pair.is_active } 
      })).unwrap();
      
      // If deactivating, close the expanded row
      if (pair.is_active && expandedRows[pair.id]) {
        setExpandedRows(prev => ({ ...prev, [pair.id]: false }));
      }
      
      // Refresh the list after toggle
      dispatch(fetchTradingPairs(filters));
    } catch (error) {
      console.error('Failed to toggle trading pair status:', error);
    }
  };

  const handleToggleExpand = (pairId) => {
    setExpandedRows(prev => ({ ...prev, [pairId]: !prev[pairId] }));
  };

  // Filter and sort trading pairs. Memoized so typing in the Add/Edit
  // dialog does not re-filter/sort the full 100+ pair list on every
  // keystroke (the dominant cause of input lag in that dialog).
  const filteredAndSortedTradingPairs = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    const filtered = search
      ? tradingPairs.filter((pair) => (
          (pair.symbol || '').toLowerCase().includes(search) ||
          (pair.target_symbol || '').toLowerCase().includes(search) ||
          (pair.origin_symbol || '').toLowerCase().includes(search) ||
          pair.target_exchange_name?.toLowerCase().includes(search) ||
          pair.origin_exchange_name?.toLowerCase().includes(search)
        ))
      : tradingPairs;
    return [...filtered].sort((a, b) => {
      if (a.is_active !== b.is_active) {
        return b.is_active ? 1 : -1;
      }
      // created_at is ISO 8601 with Z — string compare is equivalent to
      // Date compare and avoids allocating two Date objects per comparison.
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
  }, [tradingPairs, searchTerm]);

  // OrderbookDisplay lives at module scope — see top of file.

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            New Trading Pair
          </Button>
          <Box display="flex" alignItems="center" gap={1} sx={{ flexGrow: 1, maxWidth: 400 }}>
            <TextField
              size="small"
              placeholder="Search by symbol or exchange..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {searchTerm && (
              <Typography variant="body2" color="textSecondary">
                {filteredAndSortedTradingPairs.length} / {tradingPairs.length}
              </Typography>
            )}
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ width: '100%' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Status</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell>Target Exchange</TableCell>
              <TableCell>Target Quote</TableCell>
              <TableCell>Origin Exchange</TableCell>
              <TableCell>Origin Quote</TableCell>
              <TableCell>Max Orders/s</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && tradingPairs.length === 0 ? (
              // Skeleton loaders while initial data is loading
              Array(5).fill(0).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                  <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={50} height={24} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={50} height={24} /></TableCell>
                  <TableCell><Skeleton variant="text" width={30} /></TableCell>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : filteredAndSortedTradingPairs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  {searchTerm ? `No trading pairs found matching "${searchTerm}"` : 'No trading pairs found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedTradingPairs.map((pair) => {
                const connStatus = connectionStatuses[pair.id];
                const isExpanded = expandedRows[pair.id] || false;
                const hasData = hasBothOrderbookData(pair.id);
                
                return (
                  <React.Fragment key={pair.id}>
                    <TableRow 
                      sx={{ 
                        bgcolor: pair.is_active 
                          ? (hasData 
                            ? 'rgba(76, 175, 80, 0.08)' // Shallow green when both orderbooks have data
                            : 'inherit')
                          : 'rgba(255, 0, 0, 0.05)',
                        '& > *': { borderBottom: isExpanded ? 'unset' : undefined }
                      }}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleExpand(pair.id)}
                          aria-label={isExpanded ? `Collapse ${pair.symbol} details` : `Expand ${pair.symbol} details`}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                    <Tooltip title={pair.is_active ? 'Active' : 'Inactive'}>
                      <IconButton
                        onClick={() => handleToggleActive(pair)}
                        size="small"
                        aria-label={pair.is_active ? `Deactivate ${pair.symbol}` : `Activate ${pair.symbol}`}
                      >
                        {pair.is_active ?
                          <CheckCircle color="success" /> :
                          <Cancel color="disabled" />
                        }
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {/* Icon uses target_symbol (when both sides match it equals
                          the display symbol). For dual-symbol pairs we fall
                          back to whichever side has a value. */}
                      <CryptoIcon symbol={pair.target_symbol || pair.origin_symbol || pair.symbol} size={20} />
                      <Box>
                        <strong>{pair.symbol}</strong>
                        {pair.target_symbol && pair.origin_symbol && pair.target_symbol !== pair.origin_symbol && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                            {pair.target_symbol} ↔ {pair.origin_symbol}
                          </Typography>
                        )}
                      </Box>
                      {pair.enable_hedge_monitoring && !pair.is_spot_to_spot && (
                        <Tooltip title={`Hedge monitoring enabled (${(parseFloat(pair.hedge_tolerance_rate) * 100).toFixed(1)}% tolerance)`}>
                          <Chip
                            label="H"
                            size="small"
                            color="warning"
                            sx={{ height: 18, fontSize: '0.65rem', minWidth: 24 }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <ExchangeIcon exchangeName={pair.target_exchange_name} size={20} />
                      <Box>
                        <Box>{pair.target_exchange_name} ({pair.target_market_type}){pair.target_is_demo ? ' [Demo]' : ''}</Box>
                        {connStatus?.target_connected && (
                          <Chip label="Connected" size="small" color="success" sx={{ height: 16, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={pair.target_quote_asset} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <ExchangeIcon exchangeName={pair.origin_exchange_name} size={20} />
                      <Box>
                        <Box>{pair.origin_exchange_name} ({pair.origin_market_type}){pair.origin_is_demo ? ' [Demo]' : ''}</Box>
                        {connStatus?.origin_connected && (
                          <Chip label="Connected" size="small" color="success" sx={{ height: 16, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={pair.origin_quote_asset} size="small" color="secondary" />
                  </TableCell>
                  <TableCell align="center">
                    {pair.max_orders_per_s || 3}
                  </TableCell>
                  <TableCell>
                    {formatDateTimeKST(pair.created_at)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => handleOpenDialog(pair)}
                        size="small"
                        aria-label={`Edit ${pair.symbol} trading pair`}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => handleDelete(pair.id)}
                        size="small"
                        color="error"
                        aria-label={`Delete ${pair.symbol} trading pair`}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <OrderbookDisplay data={orderbookData[pair.id]} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
              )})
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <TradingPairDialog
        open={dialogOpen}
        editingPair={editingPair}
        exchanges={exchanges}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {confirmDialog}
    </Box>
  );
}

export default TradingPairs;
